import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  GoogleLogo, ArrowRight, Image as ImageIcon, Lightning, Lock,
  Stack, ArrowsClockwise, Download, CheckCircle, Sparkle,
  InstagramLogo, FacebookLogo, YoutubeLogo, LinkedinLogo, PinterestLogo, TiktokLogo, XLogo,
} from "@phosphor-icons/react";

const PLATFORM_BADGES = [
  { Icon: InstagramLogo, name: "Instagram" },
  { Icon: FacebookLogo, name: "Facebook" },
  { Icon: YoutubeLogo, name: "YouTube" },
  { Icon: XLogo, name: "X" },
  { Icon: LinkedinLogo, name: "LinkedIn" },
  { Icon: PinterestLogo, name: "Pinterest" },
  { Icon: TiktokLogo, name: "TikTok" },
];

export default function Landing() {
  const { user, login } = useAuth();

  return (
    <div data-testid="landing-page" className="grain">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-[#CCFF00]/10 blur-[120px]" />
          <div className="absolute top-40 right-0 w-[500px] h-[500px] rounded-full bg-[#FF3366]/10 blur-[120px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-20">
          <div className="chip mb-6 fade-up" data-testid="hero-badge">
            <span className="w-2 h-2 rounded-full bg-[#CCFF00]" />
            <span>v1.0 — Ad‑free, forever</span>
          </div>

          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-semibold tracking-tight leading-[0.95] max-w-4xl fade-up">
            Resize once.<br />
            <span className="text-[#CCFF00]">Post everywhere.</span>
          </h1>

          <p className="mt-6 text-lg md:text-xl text-zinc-400 max-w-2xl fade-up" style={{ animationDelay: "60ms" }}>
            A bold, ad‑free image resizer crafted for social media managers, creators, and design teams.
            Pixel‑perfect presets for every platform, processed privately in your browser.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4 fade-up" style={{ animationDelay: "120ms" }}>
            {user ? (
              <Link to="/studio" data-testid="hero-go-studio" className="btn-volt text-base">
                Open Studio <ArrowRight weight="bold" size={18} />
              </Link>
            ) : (
              <button data-testid="hero-google-login" onClick={login} className="btn-volt text-base">
                <GoogleLogo weight="bold" size={18} /> Sign in with Google
              </button>
            )}
            <Link to="/studio" data-testid="hero-try-now" className="btn-ghost text-base">
              Try the Studio <ArrowRight size={16} />
            </Link>
          </div>

          {/* Platform ticker */}
          <div className="mt-16 overflow-hidden border-y border-zinc-900 -mx-6 md:-mx-10">
            <div className="ticker-track flex gap-12 py-5 whitespace-nowrap text-zinc-500">
              {[...PLATFORM_BADGES, ...PLATFORM_BADGES, ...PLATFORM_BADGES].map((p, i) => (
                <span key={i} className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-widest">
                  <p.Icon size={18} /> {p.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DEMO PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 surface p-6 md:p-10 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#CCFF00]/5 rounded-full blur-3xl" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#CCFF00]">Studio Preview</span>
            <h2 className="font-display text-3xl md:text-4xl mt-3 leading-tight">
              The control room for your image pipeline.
            </h2>
            <p className="mt-3 text-zinc-400 max-w-xl">
              Drop in up to 10 images, pick a platform, and we'll handle the math. Auto‑crop, manual crop, fit with blur — all in one canvas.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-3">
              <div className="aspect-[1/1] rounded-xl overflow-hidden border border-zinc-800 relative">
                <img
                  src="https://images.unsplash.com/photo-1730758615609-586098e63a65?crop=entropy&cs=srgb&fm=jpg&w=600&q=80"
                  alt="Square"
                  className="w-full h-full object-cover"
                />
                <span className="absolute bottom-2 left-2 chip text-[10px]">1:1 Post</span>
              </div>
              <div className="aspect-[9/16] rounded-xl overflow-hidden border border-zinc-800 relative">
                <img
                  src="https://images.unsplash.com/photo-1622236297949-eb7f7aa3a3cc?crop=entropy&cs=srgb&fm=jpg&w=600&q=80"
                  alt="Story"
                  className="w-full h-full object-cover"
                />
                <span className="absolute bottom-2 left-2 chip text-[10px]">9:16 Story</span>
              </div>
              <div className="aspect-[16/9] rounded-xl overflow-hidden border border-zinc-800 relative">
                <img
                  src="https://images.unsplash.com/photo-1613498382159-0972b7b4c9f1?crop=entropy&cs=srgb&fm=jpg&w=600&q=80"
                  alt="Banner"
                  className="w-full h-full object-cover"
                />
                <span className="absolute bottom-2 left-2 chip text-[10px]">16:9 Banner</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 grid grid-cols-1 gap-6">
            <Feature
              Icon={Lightning}
              title="Instant, in‑browser"
              body="No uploads, no waiting. Powered by Canvas — your files never leave your device."
            />
            <Feature
              Icon={Stack}
              title="Bulk up to 10"
              body="Queue multiple files and download them as a batch. Built for the daily creator grind."
            />
            <Feature
              Icon={Lock}
              title="Private by default"
              body="100% client‑side processing. Your files never leave your device."
            />
          </div>
        </div>
      </section>

      {/* FEATURE GRID */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#CCFF00]">Capabilities</span>
            <h2 className="font-display text-3xl md:text-5xl mt-2 max-w-2xl leading-tight">
              Everything you need.<br />Nothing you don't.
            </h2>
          </div>
          <p className="text-zinc-400 max-w-md">No watermarks. No ads. No file caps. Just a fast, focused tool with the controls professionals expect.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card Icon={ImageIcon} title="11+ platforms" body="Instagram, Facebook, X, YouTube, LinkedIn, Pinterest, TikTok, Tumblr, Snapchat, Vinted, Email — all sized correctly." />
          <Card Icon={ArrowsClockwise} title="3 fit methods" body="Auto‑crop, stretch, or fit with white/black/blurred background — pick what looks best for each shot." />
          <Card Icon={Sparkle} title="Custom dimensions" body="Roll your own size. Choose JPEG, PNG, or WebP at any quality from 10 to 100." />
          <Card Icon={Stack} title="Bulk batches" body="Queue up to 10 images and process them all with the same preset in one click." />
          <Card Icon={Download} title="One‑tap download" body="Outputs are generated locally. Download instantly or save the entire batch." />
          <Card Icon={CheckCircle} title="Synced history" body="Sign in to see every image you've resized, with platform, dimensions, and a thumbnail." />
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20">
        <div className="surface p-10 md:p-16 relative overflow-hidden text-center">
          <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full bg-[#CCFF00]/8 blur-[100px]" />
          <div className="relative">
            <h2 className="font-display text-4xl md:text-6xl tracking-tight max-w-3xl mx-auto leading-[1]">
              Ready to ship cleaner posts?
            </h2>
            <p className="mt-5 text-zinc-400 max-w-xl mx-auto">
              No sign‑up required. Drop your images, pick a preset, and download in seconds.
            </p>
            <div className="mt-8 flex justify-center gap-3 flex-wrap">
              {user ? (
                <Link to="/studio" data-testid="cta-go-studio" className="btn-volt">
                  Open Studio <ArrowRight weight="bold" size={18} />
                </Link>
              ) : (
                <button data-testid="cta-google-login" onClick={login} className="btn-volt">
                  <GoogleLogo weight="bold" size={18} /> Sign in with Google
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-900 py-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-wrap items-center justify-between gap-4 text-sm text-zinc-500">
          <span className="font-mono">© {new Date().getFullYear()} ResizeStudio</span>
          <span>Crafted for creators. Zero ads, ever.</span>
        </div>
      </footer>
    </div>
  );
}

function Feature({ Icon, title, body }) {
  return (
    <div className="surface p-6 hover:border-zinc-700 transition-colors group">
      <div className="w-10 h-10 rounded-lg bg-[#CCFF00]/10 border border-[#CCFF00]/20 flex items-center justify-center mb-4 group-hover:bg-[#CCFF00]/20 transition">
        <Icon size={20} color="#CCFF00" weight="bold" />
      </div>
      <div className="font-display text-xl mb-1">{title}</div>
      <p className="text-zinc-400 text-sm leading-relaxed">{body}</p>
    </div>
  );
}

function Card({ Icon, title, body }) {
  return (
    <div className="surface p-6 hover:border-zinc-700 transition-colors group">
      <Icon size={24} color="#CCFF00" weight="duotone" />
      <div className="font-display text-lg mt-4 mb-1">{title}</div>
      <p className="text-zinc-400 text-sm leading-relaxed">{body}</p>
    </div>
  );
}
